import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DynamicQuestionnaire from "../DynamicQuestionnaire/client/DynamicQuestionnaire";
import useWebsiteAnalysis from "./hooks/useWebsiteAnalysis";
import { collection, addDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { projectOperations } from "../../utils/projectFirestore";

/**
 * OnboardingForm component
 * Main component for the client onboarding process
 * Now uses a timeline-based approach instead of step-by-step
 */
const OnboardingForm = () => {
  const [step, setStep] = useState(0);
  const [userInfo, setUserInfo] = useState({ name: "", email: "" });
  const [brandInfo, setBrandInfo] = useState({ brand: "", project: "" });
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {
    analyzeWebsite,
    analysisResults,
    isLoading: isAnalyzing,
    analyzeError,
  } = useWebsiteAnalysis();

  // Track the analyzed website URL
  const [analyzedWebsiteUrl, setAnalyzedWebsiteUrl] = useState("");

  // Helper to create or fetch user
  const createOrFetchUser = async (name, email) => {
    // Check if user exists by email
    const q = query(collection(db, "users"), where("email", "==", email));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }
    // Create new user
    const docRef = await addDoc(collection(db, "users"), { name, email });
    return docRef.id;
  };

  // Handle initial form submit
  const handleInitialSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const uid = await createOrFetchUser(userInfo.name, userInfo.email);
      setUserId(uid);
      setStep(1);
    } catch (err) {
      toast.error("Failed to start onboarding. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handlers for DynamicQuestionnaire
  const handleAnalyzeUrl = async (url) => {
    try {
      setAnalyzedWebsiteUrl(url); // Store the analyzed URL
      await analyzeWebsite(url);
    } catch {}
  };
  const handleSubmit = async (data) => {
    toast.success("Questionnaire submitted successfully!");
    setTimeout(() => navigate("/"), 2000);
    return true;
  };
  const handleComplete = (formattedData) => {
    toast.success("Questionnaire completed! Data ready for brand profile.");
  };

  if (step === 0) {
    return (
      <div className="bg-[#111111] min-h-screen flex items-center justify-center">
        <form
          onSubmit={handleInitialSubmit}
          className="bg-white/5 p-8 rounded-lg shadow-lg max-w-md w-full space-y-6 border border-white/10"
        >
          <h2 className="text-2xl font-bold text-white mb-4">
            Start Onboarding
          </h2>
          <div>
            <label className="block text-white/80 mb-1">Your Name</label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 rounded bg-[#222] text-white border border-white/10"
              value={userInfo.name}
              onChange={(e) =>
                setUserInfo({ ...userInfo, name: e.target.value })
              }
            />
          </div>
          <div>
            <label className="block text-white/80 mb-1">Email</label>
            <input
              type="email"
              required
              className="w-full px-3 py-2 rounded bg-[#222] text-white border border-white/10"
              value={userInfo.email}
              onChange={(e) =>
                setUserInfo({ ...userInfo, email: e.target.value })
              }
            />
          </div>
          <div>
            <label className="block text-white/80 mb-1">
              Brand/Company Name
            </label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 rounded bg-[#222] text-white border border-white/10"
              value={brandInfo.brand}
              onChange={(e) =>
                setBrandInfo({ ...brandInfo, brand: e.target.value })
              }
            />
          </div>
          <div>
            <label className="block text-white/80 mb-1">
              Project Name/Keyword
            </label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 rounded bg-[#222] text-white border border-white/10"
              value={brandInfo.project}
              onChange={(e) =>
                setBrandInfo({ ...brandInfo, project: e.target.value })
              }
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded bg-primary-green text-black font-bold mt-4 hover:bg-primary-green/90 transition"
          >
            {loading ? "Starting..." : "Begin Questionnaire"}
          </button>
        </form>
      </div>
    );
  }

  // Step 1: Show DynamicQuestionnaire
  return (
    <div className="bg-[#111111] min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <DynamicQuestionnaire
            userId={userId}
            onSubmit={handleSubmit}
            onAnalyzeUrl={handleAnalyzeUrl}
            onComplete={handleComplete}
            analysisResults={analysisResults}
            isAnalyzing={isAnalyzing}
            analyzeError={analyzeError}
            websiteUrl={analyzedWebsiteUrl}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default OnboardingForm;
